import { useMeasure } from "react-use";
import Widget from "../../../../../components/baseWidget";
import { useWidgetContext } from "../../../../../helpers/hooks/useWidgetContext";
import { useMemo } from "react";
import BaseChart from "../../../../../components/baseChart";
// Import ECharts types
import type { EChartsOption } from "echarts";

// Define types for our data
interface MenuItemData {
  value: number[];
  name: string;
  symbolSize: number;
  itemStyle?: { color: string };
  symbol?: string;
}

const ScatterPlot = ({ width, height }: { width: number; height: number }) => {
  const option = useMemo(() => {
    // Define our data by category
    const starData: MenuItemData[] = [
      {
        value: [25, 25],
        name: "Filet Mignon",
        symbolSize: 20,
      },
      {
        value: [20, 20],
        name: "Lobster Tail",
        symbolSize: 20,
      },
      {
        value: [18, 23],
        name: "Truffle Pasta",
        symbolSize: 20,
      },
    ];

    const puzzleData: MenuItemData[] = [
      {
        value: [22, -15],
        name: "Foie Gras",
        symbolSize: 20,
      },
      {
        value: [15, -20],
        name: "Caviar Toast",
        symbolSize: 20,
      },
      {
        value: [15, -30],
        name: "Wagyu Tartare",
        symbolSize: 20,
      },
    ];

    const plowhorseData: MenuItemData[] = [
      {
        value: [-15, 20],
        name: "Burger",
        symbolSize: 20,
      },
      {
        value: [-20, 15],
        name: "Chicken Sandwich",
        symbolSize: 20,
      },
      {
        value: [-25, 25],
        name: "Pizza",
        symbolSize: 20,
      },
    ];

    const dogData: MenuItemData[] = [
      {
        value: [-20, -20],
        name: "Side Salad",
        symbolSize: 20,
      },
      {
        value: [-25, -30],
        name: "Plain Toast",
        symbolSize: 20,
      },
      {
        value: [-30, -15],
        name: "House Water",
        symbolSize: 20,
      },
    ];

    return {
      legend: {
        show: true,
        type: "plain",
        top: 5,
        left: 'center',
        itemHeight: 12,
        itemWidth: 12,
        itemGap: 15,
        formatter: "███",
        textStyle: {
          fontSize: 12
        }
      },
      grid: {
        containLabel: true,
        left: 10,
        right: 10,
        top: 50,
        bottom: 45,
      },
      tooltip: {
        show: false,
        trigger: "item",
        formatter: function (params: any) {
          const profitValue = params.data.value[0];
          const popularityValue = params.data.value[1];
          const profitDisplay =
            profitValue > 0 ? `+${profitValue}` : profitValue;
          const popularityDisplay =
            popularityValue > 0 ? `+${popularityValue}` : popularityValue;

          return `${params.data.name}<br/>Profit: ${profitDisplay}<br/>Popularity: ${popularityDisplay}`;
        },
      },
      xAxis: {
        type: "value",
        name: "███",
        nameLocation: "middle",
        nameGap: 30,
        min: -40,
        max: 40,
        splitLine: {
          lineStyle: {
            type: "dashed",
          },
        },
        axisLine: {
          lineStyle: {
            color: "#999",
          },
        },
        axisLabel: {
          formatter: "███",
        },
      },
      yAxis: {
        type: "value",
        name: "██████",
        nameLocation: "middle",
        nameGap: 30,
        min: -40,
        max: 40,
        splitLine: {
          lineStyle: {
            type: "dashed",
          },
        },
        axisLine: {
          lineStyle: {
            color: "#999",
          },
        },
        axisLabel: {
          formatter: "███",
        },
      },
      series: [
        {
          name: "Star",
          type: "scatter",
          symbol: "diamond",
          itemStyle: {
            color: "#5470c6",
          },
          data: starData,
          markLine: {
            silent: true,
            lineStyle: {
              color: "#333",
              type: "dashed",
            },
            data: [{ xAxis: 0 }, { yAxis: 0 }],
            label: {
              formatter: "",
            },
            symbol: ["none", "none"],
          },
          markArea: {
            silent: true,
            itemStyle: {
              opacity: 0.2,
              color: "#33333333",
            },
            data: [
              [
                { xAxis: 0, yAxis: 0 },
                { xAxis: 40, yAxis: 40 },
              ],
              [
                { xAxis: 0, yAxis: -40 },
                { xAxis: 40, yAxis: 0 },
              ],
              [
                { xAxis: -40, yAxis: 0 },
                { xAxis: 0, yAxis: 40 },
              ],
              [
                { xAxis: -40, yAxis: -40 },
                { xAxis: 0, yAxis: 0 },
              ],
            ],
          },
        },
        {
          name: "Puzzle",
          type: "scatter",
          symbol: "rect",
          itemStyle: {
            color: "#91cc75",
          },
          data: puzzleData,
        },
        {
          name: "Plow-horse",
          type: "scatter",
          symbol: "triangle",
          itemStyle: {
            color: "#fac858",
          },
          data: plowhorseData,
        },
        {
          name: "Dog",
          type: "scatter",
          symbol: "circle",
          itemStyle: {
            color: "#ee6666",
          },
          data: dogData,
        },
      ],
    } as EChartsOption;
  }, []);

  return (
    <BaseChart
      options={option}
      chartType="scatter"
      width={width}
      height={height}
    />
  );
};

const MenuEngineeringWidgetSample = () => {
  const widget = useWidgetContext();
  const title = widget.widgetData?.title;
  const [ref, { width, height }] = useMeasure();

  return (
    <Widget ref={ref}>
      <Widget.Header>
        <Widget.Title>{title}</Widget.Title>
      </Widget.Header>
      <Widget.Content>
        <ScatterPlot width={width} height={height} />
      </Widget.Content>
    </Widget>
  );
};

export default MenuEngineeringWidgetSample;
